<template>
  <div class="page-container detail-page">
    <v-snackbar v-model="showSnackBar" top right :timeout="4000">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <vue-element-loading :active="busy" :is-full-screen="true" background-color="rgba(240,253,244,0.7)" color="#15803d" spinner="spinner" />

    <div v-if="loading" class="surface pa-6">
      <v-skeleton-loader type="article, table-row@3"></v-skeleton-loader>
    </div>

    <div v-else-if="!app" class="surface empty-state">
      <v-icon size="52">mdi-file-alert-outline</v-icon>
      <div class="cell-strong">Application not found</div>
      <v-btn depressed class="btn-act btn-act--neutral mt-2" @click="$router.go(-1)">Go back</v-btn>
    </div>

    <template v-else>
      <PageHeader :title="'Application #' + (app.applicationNo || '')" :subtitle="app.natureOfApplication" icon="mdi-movie-open-outline">
        <template v-slot:actions>
          <v-btn depressed class="btn-act btn-act--info" @click="timelineDialog = true">
            <v-icon left small>mdi-timeline-clock-outline</v-icon>
            Timeline
          </v-btn>
          <v-btn depressed class="btn-act btn-act--neutral" @click="$router.go(-1)">
            <v-icon left small>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </template>
      </PageHeader>

      <!-- Where the application is -->
      <div class="where-bar">
        <span class="status-pill" :class="'status-pill--' + status.tone">{{ status.label }}</span>
        <!-- Where it is, unless that is the viewer's own desk ("At your desk") -->
        <span v-if="!canAct" class="where-chip">
          <v-icon x-small>mdi-map-marker-path</v-icon>
          {{ whereLabel }}
        </span>
        <span v-if="app.isExtended && app.originalApplication" class="where-chip where-chip--amber">
          <v-icon x-small>mdi-calendar-expand-horizontal</v-icon>
          Extension of #{{ app.originalApplication.applicationNo }}
        </span>
        <span v-if="canAct" class="where-chip where-chip--green">
          <v-icon x-small>mdi-account-check-outline</v-icon>
          At your desk
        </span>
        <span v-else-if="isDeskMode && !cancelPending" class="where-chip">
          <v-icon x-small>mdi-eye-outline</v-icon>
          View only — not at your desk
        </span>
      </div>

      <!-- Your task: what to do at this desk, and the buttons it unlocks -->
      <section v-if="taskGuide" class="surface tg-card mb-5">
        <div class="tg-head">
          <span class="tg-icon"><v-icon color="white">mdi-clipboard-check-outline</v-icon></span>
          <div class="tg-head-text">
            <div class="tg-kicker">Your task · {{ taskGuide.tag }}</div>
            <div class="tg-title">{{ taskGuide.title }}</div>
            <div v-if="taskGuide.hint" class="tg-hint">{{ taskGuide.hint }}</div>
          </div>
        </div>

        <ol v-if="taskGuide.steps.length" class="tg-steps">
          <li
            v-for="(st, i) in taskGuide.steps"
            :key="st.label"
            class="tg-step"
            :class="{ 'tg-step--done': st.done === true, 'tg-step--optional': st.done === null }"
          >
            <span class="tg-step-dot">
              <v-icon v-if="st.done === true" x-small color="white">mdi-check</v-icon>
              <template v-else>{{ i + 1 }}</template>
            </span>
            <div class="tg-step-text">
              <div class="tg-step-label">
                {{ st.label }}
                <span v-if="st.done === null" class="tg-optional">Optional</span>
              </div>
              <div class="tg-step-sub">{{ st.sub }}</div>
              <!-- Range offices by name and what each has uploaded -->
              <ul v-if="st.ranges && st.ranges.length" class="tg-ranges">
                <li v-for="r in st.ranges" :key="r.id" :class="{ 'tg-range--done': r.mahazar && r.report }">
                  <v-icon x-small>{{ r.mahazar && r.report ? "mdi-check-circle" : "mdi-clock-outline" }}</v-icon>
                  <span class="tg-range-name">{{ r.name }}<template v-if="r.you"> (you)</template></span>
                  <span class="tg-range-state">{{ rangeUploadLabel(r) }}</span>
                </li>
              </ul>
              <!-- Do the step right here -->
              <div v-if="st.buttons && st.buttons.length" class="tg-step-buttons">
                <v-btn
                  v-for="b in st.buttons"
                  :key="b.label"
                  depressed
                  x-small
                  class="btn-act btn-act--sm"
                  :class="'btn-act--' + (b.tone || 'teal-soft')"
                  @click="b.run()"
                >
                  <v-icon left x-small>{{ b.icon }}</v-icon>
                  {{ b.label }}
                </v-btn>
              </div>
            </div>
          </li>
        </ol>

        <div v-if="taskGuide.next.length" class="tg-next">
          <span class="tg-next-label">{{ taskGuide.steps.length ? "Then" : "Next" }}</span>
          <span
            v-for="n in taskGuide.next"
            :key="n.label"
            class="tg-action"
            :class="n.ready ? 'tg-action--ready' : 'tg-action--locked'"
          >
            <v-icon x-small>{{ n.ready ? "mdi-check-circle" : "mdi-lock-outline" }}</v-icon>
            {{ n.label }}
          </span>
          <span class="tg-note">{{ taskGuide.note }}</span>
        </div>
      </section>

      <!-- Cancellation requested by the applicant -->
      <section v-if="cancelPending" class="cr-banner">
        <div class="cr-banner-icon"><v-icon>mdi-file-cancel-outline</v-icon></div>
        <div class="cr-banner-body">
          <div class="cr-banner-top">
            <span class="cr-banner-title">The applicant has asked to cancel this application</span>
            <span v-if="app.cancelRequestNumber && app.cancelRequestNumber !== '0'" class="cr-ref">Request #{{ app.cancelRequestNumber }}</span>
          </div>
          <div class="cr-reason">
            <span class="cr-reason-label">Reason given</span>
            <p>{{ hasCancelReason ? app.reason : "No reason was recorded." }}</p>
          </div>
          <ul class="cr-outcomes">
            <li><v-icon x-small color="#b91c1c">mdi-close-circle</v-icon><b>Accept</b> — the application is closed as cancelled. This can't be undone.</li>
            <li><v-icon x-small color="#15803d">mdi-check-circle</v-icon><b>Deny</b> — the application stays active and continues from {{ whereNow }}.</li>
          </ul>
          <div v-if="app.paymentStatus === 'Success'" class="cr-paid">
            <v-icon x-small>mdi-cash-check</v-icon>
            The applicant has already paid ₹{{ money(totalPaid) }} for this application.
          </div>
        </div>
      </section>

      <!-- Refund of a cancelled, paid application -->
      <section v-if="refundTrackable" class="surface pa-5 mb-5 rf-panel">
        <div class="rf-head">
          <h2 class="section-title ma-0"><v-icon>mdi-cash-refund</v-icon> Refund</h2>
          <v-btn v-if="canRecordRefund" depressed class="btn-act btn-act--success" @click="openRefundDialog">
            <v-icon left small>mdi-cash-check</v-icon>
            Record refund &amp; close
          </v-btn>
        </div>
        <ol class="rf-steps">
          <li v-for="(st, i) in refundSteps" :key="st.label" :class="{ 'rf-done': i < refundStep, 'rf-now': i === refundStep }">
            <span class="rf-dot"><v-icon v-if="i < refundStep" x-small color="white">mdi-check</v-icon><template v-else>{{ i + 1 }}</template></span>
            <div>
              <div class="rf-label">{{ st.label }}</div>
              <div class="rf-sub">{{ st.sub }}</div>
            </div>
          </li>
        </ol>
        <p v-if="app.refundStatus === 'Completed'" class="rf-paid">
          Refunded ₹{{ money(app.refundAmount || totalPaid) }} by {{ app.refundtype }} no. {{ app.refundtypenumber }}
        </p>
      </section>

      <div class="detail-grid">
        <div class="detail-main">
          <!-- Applicant -->
          <section class="surface pa-5 mb-5">
            <h2 class="section-title"><v-icon>mdi-account-outline</v-icon> Applicant</h2>
            <div class="info-grid">
              <div v-for="f in applicantFields" :key="f.label" class="info-item">
                <div class="info-label">{{ f.label }}</div>
                <div class="info-value">{{ f.value || "—" }}</div>
              </div>
            </div>
          </section>

          <!-- Locations -->
          <section class="surface mb-5">
            <div class="surface-toolbar">
              <h2 class="section-title ma-0"><v-icon>mdi-map-marker-multiple-outline</v-icon> Shooting locations</h2>
              <span class="member-count">{{ locations.length }} location{{ locations.length === 1 ? "" : "s" }}</span>
            </div>
            <div class="table-scroll">
              <v-simple-table class="app-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Film</th>
                    <th>Dates</th>
                    <th>Days</th>
                    <th>Crew</th>
                    <th>Vehicles</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(loc, i) in locations" :key="i">
                    <td data-label="Location"><span class="cell-strong">{{ loc.location }}</span></td>
                    <td data-label="Film">{{ loc.filmName }}</td>
                    <td data-label="Dates">{{ formatDate(loc.fromDate) }} – {{ formatDate(loc.toDate) }}</td>
                    <td data-label="Days">
                      <div>
                        <div>{{ loc.shootingDuration }} shooting</div>
                        <div v-if="loc.installationDays" class="cell-muted">{{ loc.installationDays }} installation</div>
                      </div>
                    </td>
                    <td data-label="Crew">{{ loc.noOfMembers }}</td>
                    <td data-label="Vehicles">{{ loc.noOfVehicles }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </section>

          <!-- Animals -->
          <section v-if="app.animalinvolved === 'YES'" class="surface pa-5 mb-5">
            <h2 class="section-title"><v-icon>mdi-paw</v-icon> Animals involved</h2>
            <div class="info-grid">
              <div v-for="(a, i) in animalList" :key="'animal' + i" class="info-item">
                <div class="info-label">Animal {{ i + 1 }}</div>
                <div class="info-value">{{ a.species }} × {{ a.count }}</div>
              </div>
              <div class="info-item"><div class="info-label">Total animals</div><div class="info-value">{{ app.noOfAnimal || "—" }}</div></div>
              <div class="info-item"><div class="info-label">Pre-shoot number</div><div class="info-value">{{ app.preshootNumber || "—" }}</div></div>
              <div class="info-item"><div class="info-label">Valid from</div><div class="info-value">{{ formatDate(app.validityfromDate) }}</div></div>
              <div class="info-item"><div class="info-label">Valid to</div><div class="info-value">{{ formatDate(app.validitytoDate) }}</div></div>
            </div>
          </section>

          <!-- Documents: uploaded files as tiles, then what can be generated or uploaded -->
          <section class="surface mb-5">
            <div class="surface-toolbar">
              <h2 class="section-title ma-0"><v-icon>mdi-folder-file-outline</v-icon> Documents</h2>
              <span class="member-count">{{ documents.length }} file{{ documents.length === 1 ? "" : "s" }}</span>
            </div>
            <div class="pa-5">
              <div v-if="documents.length" class="doc-list">
                <a v-for="d in documents" :key="d.label" :href="fileUrl(d.key)" target="_blank" rel="noopener" class="doc-link">
                  <v-icon small color="#15803d">mdi-file-document-outline</v-icon>
                  <span class="doc-link-label">{{ d.label }}</span>
                  <v-icon x-small class="ml-auto">mdi-open-in-new</v-icon>
                </a>
              </div>
              <div v-else class="cell-muted">No uploaded documents yet.</div>

              <div class="doc-actions">
                <div>
                  <div class="doc-title">Generated PDFs</div>
                  <div class="pdf-grid">
                    <v-btn depressed small class="btn-act btn-act--sm btn-act--doc" @click="openPdf('/application/pdf')">Application</v-btn>
                    <v-btn depressed small class="btn-act btn-act--sm btn-act--doc" @click="openPdf('/acknowledgement/pdf')">Acknowledgement</v-btn>
                    <v-btn v-if="app.paymentInitiation === 'YES'" depressed small class="btn-act btn-act--sm btn-act--doc" @click="openPdf('/new/agreement/pdf')">Agreement</v-btn>
                    <template v-if="canGenerateOrders">
                      <v-btn depressed small class="btn-act btn-act--sm btn-act--doc" @click="openPdf('/new/proceedings/pdf')">Proceedings</v-btn>
                      <v-btn depressed small class="btn-act btn-act--sm btn-act--doc" @click="openPdf('/new/licence/pdf')">Licence</v-btn>
                      <v-btn depressed small class="btn-act btn-act--sm btn-act--doc" @click="openPdf('/letterto/range/pdf')">Letter to range</v-btn>
                    </template>
                  </div>
                </div>

                <!-- Uploads (at the viewer's desk these are in the task card) -->
                <div v-if="uploadSlots.length && !taskGuide">
                  <div class="doc-title">Upload signed documents</div>
                  <div class="pdf-grid">
                    <v-btn
                      v-for="slot in uploadSlots"
                      :key="slot.field"
                      depressed
                      small
                      class="btn-act btn-act--sm btn-act--teal-soft"
                      @click="openUpload(slot)"
                    >
                      <v-icon left x-small>mdi-upload</v-icon>
                      {{ app[slot.field] ? "Replace" : "Upload" }} {{ slot.label.toLowerCase() }}
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Payment history -->
          <section v-if="showPayments" class="surface mb-5">
            <div class="surface-toolbar">
              <h2 class="section-title ma-0"><v-icon>mdi-bank-outline</v-icon> eTreasury payments</h2>
            </div>
            <div v-if="!payments.length" class="empty-state">No eTreasury payment history.</div>
            <div v-else class="table-scroll">
              <v-simple-table class="app-table">
                <thead>
                  <tr><th>Date</th><th>Department ID</th><th>GRN</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(txn, i) in payments" :key="i">
                    <td data-label="Date">{{ formatDate(txn.paymentInitiateDate) }}</td>
                    <td data-label="Department ID">{{ txn.departmentId || "—" }}</td>
                    <td data-label="GRN">{{ txn.GRN || "—" }}</td>
                    <td data-label="Amount">₹{{ money(txn.totalAmount || txn.AMOUNT) }}</td>
                    <td data-label="Status">
                      <span class="status-pill" :class="txn.status === 'Success' ? 'status-pill--success' : 'status-pill--warning'">{{ txn.status || "—" }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </section>
        </div>

        <aside class="detail-side">
          <!-- Range offices it was sent to and what each has uploaded (a range
               officer at their desk sees this in the task card) -->
          <section v-if="rangeProgress.length && !(taskGuide && effectiveMode === 'range')" class="surface pa-5 mb-5">
            <h2 class="section-title"><v-icon>mdi-map-marker-radius-outline</v-icon> Range offices</h2>
            <div v-for="r in rangeProgress" :key="r.id" class="fee-row">
              <span>{{ r.name }}<template v-if="r.you"> (you)</template></span>
              <b :class="r.mahazar && r.report ? 'rng-ok' : 'rng-wait'">{{ rangeUploadLabel(r) }}</b>
            </div>
          </section>

          <!-- Fees -->
          <section ref="feesPanel" class="surface pa-5 mb-5">
            <h2 class="section-title"><v-icon>mdi-cash-multiple</v-icon> Fees</h2>
            <div class="fee-row"><span>Shooting fee</span><b>₹{{ money(app.shootingfee) }}</b></div>
            <div class="fee-row"><span>Installation fee</span><b>₹{{ money(app.installationfee) }}</b></div>
            <div v-if="app.refundAmount" class="fee-row"><span>Refund amount</span><b>₹{{ money(app.refundAmount) }}</b></div>
            <div class="fee-row"><span>Payment</span><b>{{ app.paymentStatus === "Success" ? "Paid" : (app.paymentInitiation === "YES" ? "Awaiting payment" : "Not started") }}</b></div>
            <div v-if="app.refundStatus && app.refundStatus !== 'NoRefund'" class="fee-row"><span>Refund</span><b>{{ app.refundStatus }}</b></div>

            <!-- Fee editor (desk roles holding the application) -->
            <div v-if="canEditFees" class="fee-editor">
              <div class="fee-editor-title">Update fees</div>
              <template v-if="app.paymentStatus !== 'Success'">
                <v-text-field v-model.number="fees.shootingfee" type="number" min="0" outlined dense hide-details label="Shooting fee (₹)" class="mb-3"></v-text-field>
                <v-text-field v-model.number="fees.installationfee" type="number" min="0" outlined dense hide-details label="Installation fee (₹)" class="mb-3"></v-text-field>
              </template>
              <v-text-field v-if="hasRangeDocs && !refundDone" v-model.number="fees.refundAmount" type="number" min="0" outlined dense hide-details label="Refund amount (₹)" class="mb-3"></v-text-field>
              <v-btn depressed block class="btn-act btn-act--primary" @click="submitFees">
                <v-icon left small>mdi-content-save-outline</v-icon>
                Save fees
              </v-btn>
            </div>
          </section>

        </aside>
      </div>

      <!-- Actions -->
      <div v-if="actions.length || waitingForPayment || cancelPending || canMarkPaid" class="sticky-actions surface">
        <span v-if="waitingForPayment" class="where-chip where-chip--amber mr-auto">
          <v-icon x-small>mdi-timer-sand</v-icon>
          Waiting for the applicant's payment
        </span>
        <!-- Why a button is still locked -->
        <span v-if="lockedNote" class="where-chip where-chip--amber mr-auto">
          <v-icon x-small>mdi-lock-outline</v-icon>
          {{ lockedNote }}
        </span>
        <v-btn v-if="canMarkPaid" depressed class="btn-act btn-act--success" @click="openMarkPaid">
          <v-icon left small>mdi-cash-check</v-icon>
          Mark as paid
        </v-btn>
        <template v-if="cancelPending">
          <span class="cr-decide mr-auto">Your decision on the cancellation request</span>
          <v-btn depressed class="btn-act btn-act--neutral" @click="openDecision('deny')">
            <v-icon left small>mdi-undo-variant</v-icon>
            Deny request
          </v-btn>
          <v-btn depressed class="btn-act btn-act--danger-solid" @click="openDecision('accept')">
            <v-icon left small>mdi-cancel</v-icon>
            Accept &amp; cancel application
          </v-btn>
        </template>
        <v-btn
          v-for="a in actions"
          :key="a.key"
          depressed
          class="btn-act"
          :class="'btn-act--' + a.tone"
          :disabled="a.disabled"
          @click="a.run()"
        >
          <v-icon left small>{{ a.disabled ? "mdi-lock-outline" : a.icon }}</v-icon>
          {{ a.label }}
        </v-btn>
      </div>
    </template>

    <!-- Mark as paid (super admin) -->
    <v-dialog v-model="markPaidDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Mark as paid</div>
          <div class="dialog-head-subtitle">Application #{{ app && app.applicationNo }}</div>
        </div>
        <div class="pa-5">
          <p class="note-warn">This records the payment as received without an eTreasury confirmation. The reason is added to the timeline.</p>
          <v-textarea v-model="reason" outlined dense rows="3" auto-grow label="Reason (e.g. GRN / bank reference)" hide-details></v-textarea>
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="markPaidDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--success" :disabled="busy" @click="submitMarkPaid">Mark as paid</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancellation decision -->
    <v-dialog v-model="decisionDialog" max-width="520px" :persistent="busy">
      <v-card v-if="decision" class="dialog-card">
        <div class="cr-dialog-head" :class="'cr-dialog-head--' + decision">
          <span class="cr-dialog-icon"><v-icon color="white">{{ decision === "accept" ? "mdi-cancel" : "mdi-undo-variant" }}</v-icon></span>
          <div>
            <div class="cr-dialog-title">{{ decision === "accept" ? "Accept the cancellation?" : "Deny the cancellation?" }}</div>
            <div class="cr-dialog-sub">#{{ app && app.applicationNo }}</div>
          </div>
        </div>
        <v-card-text class="pt-5">
          <p class="cr-dialog-text">
            <template v-if="decision === 'accept'">The application will be closed as <b>cancelled</b>. This can't be undone.</template>
            <template v-else>The application stays <b>active</b> and continues from {{ whereNow }}.</template>
          </p>
          <v-textarea v-model.trim="remarks" outlined auto-grow rows="3" hide-details label="Remarks (optional)" placeholder="Shown in the application's timeline"></v-textarea>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" :disabled="busy" @click="decisionDialog = false">Go back</v-btn>
          <v-btn depressed class="btn-act" :class="decision === 'accept' ? 'btn-act--danger-solid' : 'btn-act--primary'" :loading="busy" @click="submitDecision">
            {{ decision === "accept" ? "Cancel application" : "Deny request" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Forward / return -->
    <v-dialog v-model="forwardDialog" max-width="520" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">{{ forwardTitle }}</div>
          <div class="dialog-head-subtitle">Application #{{ app && app.applicationNo }}</div>
        </div>
        <div class="pa-5">
          <p v-if="mode === 'superintendent' && app && app.paymentStatus !== 'Success'" class="note-warn">
            Security deposit should be remitted as a Demand Draft; enter the amount in the remarks.
          </p>
          <div v-if="forwardTarget" class="fwd-to">
            <span class="fwd-to-icon" :class="'fwd-to-icon--' + (forwardTarget.person ? 'person' : 'desk')">
              <v-icon small>{{ forwardTarget.person ? "mdi-account-outline" : "mdi-office-building-outline" }}</v-icon>
            </span>
            <div>
              <div class="fwd-to-name">{{ forwardTarget.person ? forwardTarget.name || "Applicant" : forwardTarget.label + " desk" }}</div>
              <div class="fwd-to-sub">
                {{ forwardTarget.person ? "The applicant can edit and resubmit the application." : "Any " + forwardTarget.label + " of the division can take it up." }}
              </div>
            </div>
          </div>
          <v-textarea v-model="reason" outlined dense rows="3" auto-grow label="Remarks" hide-details></v-textarea>
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="forwardDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--primary" :disabled="busy" @click="submitForward">
            <v-icon left small>mdi-send-outline</v-icon>
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Range offices -->
    <v-dialog v-model="rangeDialog" max-width="560" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">{{ hasRangeDocs ? "Send back to range offices" : "Forward to range offices" }}</div>
          <div class="dialog-head-subtitle">The selected range officers inspect the locations and upload their mahazar and report.</div>
        </div>
        <div class="pa-5">
          <p v-if="hasRangeDocs" class="note-warn">Sending back clears the mahazars and reports already uploaded.</p>
          <v-autocomplete
            v-model="selectedRanges"
            :items="ranges"
            item-text="officeName"
            item-value="_id"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            label="Range offices"
            no-data-text="No range offices found under this division"
          ></v-autocomplete>
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="rangeDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--primary" :disabled="busy" @click="submitRangeForward">
            <v-icon left small>mdi-send-outline</v-icon>
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject -->
    <v-dialog v-model="rejectDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head dialog-head--danger">
          <div class="dialog-head-title">Reject application</div>
          <div class="dialog-head-subtitle">The applicant is told the reason.</div>
        </div>
        <div class="pa-5">
          <v-textarea v-model="reason" outlined dense rows="3" auto-grow label="Reason for rejection" hide-details></v-textarea>
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="rejectDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--danger-solid" :disabled="busy" @click="submitReject">Reject</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payment initiation -->
    <v-dialog v-model="proceedDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Proceed to fee payment</div>
          <div class="dialog-head-subtitle">The applicant will be asked to pay the fees and execute the agreement.</div>
        </div>
        <div class="pa-5 cell-muted">
          Shooting fee ₹{{ money(app && app.shootingfee) }} · Installation fee ₹{{ money(app && app.installationfee) }}
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="proceedDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--success" :disabled="busy" @click="submitProceed">Proceed</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Paid while still at stage 1: on to the Section Clerk -->
    <v-dialog v-model="clerkDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Send to Section Clerk</div>
          <div class="dialog-head-subtitle">The fees are paid. The Section Clerk uploads the executed agreement and forwards it to the Superintendent, then it comes back to you to forward to the range offices.</div>
        </div>
        <v-card-actions class="px-5 py-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="clerkDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--primary" :disabled="busy" @click="submitToClerk">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Every range office is done: on to the Section Clerk -->
    <v-dialog v-model="rangeDoneDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Forward to Section Clerk</div>
          <div class="dialog-head-subtitle">Every range office has uploaded its mahazar and report. Only one range office needs to send it; the Section Clerk takes it up for the security deposit refund.</div>
        </div>
        <v-card-actions class="px-5 py-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="rangeDoneDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--primary" :disabled="busy" @click="submitRangeDone">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Refund -->
    <v-dialog v-model="refundDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">{{ refundTrackable ? "Record refund" : "Approve refund" }}</div>
          <div class="dialog-head-subtitle">
            {{ refundTrackable ? "Refund ₹" + money((app && app.refundAmount) || totalPaid) + " of the cancelled application." : "Refund ₹" + money(app && app.refundAmount) + " of the security deposit." }}
          </div>
        </div>
        <div class="pa-5">
          <v-select v-model="refundtype" :items="['DD', 'Cheque']" outlined dense label="Refund by" class="mb-3"></v-select>
          <v-text-field v-model="refundtypenumber" outlined dense label="DD / Cheque number (6 digits)" maxlength="6" hide-details></v-text-field>
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="refundDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--success" :disabled="busy" @click="submitRefund">Approve refund</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Upload -->
    <v-dialog v-model="uploadDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Upload {{ uploadSlot && uploadSlot.label.toLowerCase() }}</div>
          <div class="dialog-head-subtitle">PDF, JPG or PNG</div>
        </div>
        <div class="pa-5">
          <v-file-input v-model="uploadFile" outlined dense accept="application/pdf,image/jpeg,image/png" label="Choose file" prepend-icon="" prepend-inner-icon="mdi-paperclip" hide-details></v-file-input>
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" @click="uploadDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--primary" :disabled="busy || !uploadFile" @click="submitUpload">Upload</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <TimelineDialog v-model="timelineDialog" :application="app || {}" />
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader.vue";
import TimelineDialog from "@/components/Common/TimelineDialog.vue";
import { desks, stagesFor, statusOf, TASK_LABELS } from "@/components/Common/deskConfig";
import { getUserType, isSuperAdmin } from "@/components/Common/navConfig";

// Where each role can send an application. Each group is
// [list key, single key, desk label, verb]; the backend delivers to the desk
// (role + office) of the user id sent, so one user per role is enough.
const TARGET_SOURCES = {
  clerk: { url: "/clerk/forward/list", groups: [["superintendents", "superintendent", "Superintendent", "Forward to"]] },
  superintendent: {
    url: "/superintendent/forward/list",
    groups: [
      ["clerks", "clerk", "Section Clerk", "Send back to"],
      ["dfos", "dfo", "DFO", "Forward to"],
    ],
  },
  division: { url: "/dfo/forward/list", groups: [["superintendents", "superintendent", "Superintendent", "Return to"]], applicant: true },
};

// Files a desk uploads. Range files are saved first, then confirmed by key
// (the endpoints the old range page used).
const UPLOADS = {
  userAgreement: { field: "userAgreement", label: "Agreement", url: "/user/agreement" },
  proceedings: { field: "proceedings", label: "Proceedings", url: "/upload/documents", pdf: "/new/proceedings/pdf" },
  license: { field: "license", label: "Licence", url: "/upload/documents", pdf: "/new/licence/pdf" },
  letterToRange: { field: "letterToRange", label: "Letter to range", url: "/upload/documents", pdf: "/letterto/range/pdf" },
  mahazar: { field: "tempMahassar", label: "Mahazar", url: "/temp/mahassar", confirmUrl: "/mahassar/upload" },
  report: { field: "tempReport", label: "Report", url: "/temp/report", confirmUrl: "/report/upload" },
};

// "A", "A and B", "A, B and C"
function joinNames(names) {
  if (names.length < 2) return names[0] || "";
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

// One colour per destination, as on Nature Camp, so no two buttons look alike.
const TARGET_TONES = {
  Applicant: "violet",
  "Section Clerk": "cyan",
  Superintendent: "orange",
  DFO: "indigo",
};

// Detail page of a film shooting application for division-office staff,
// range officers and admins. `mode` is the desk the page was opened from;
// actions only appear while the application is waiting at the viewer's desk
// (canAct from the API).
export default {
  name: "ApplicationDetail",
  components: { PageHeader, TimelineDialog },
  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => ["clerk", "superintendent", "division", "range", "admin"].includes(v),
    },
  },
  data() {
    return {
      id: this.$route.query.id,
      app: null,
      canAct: false,
      // Range officers: { total, mahazars, reports, mahazarDone, reportDone, allDone }
      rangeState: null,
      // Every range office it was sent to: [{ id, name, you, mahazar, report }]
      rangeProgress: [],
      loading: true,
      busy: false,
      msg: "",
      showSnackBar: false,
      fees: { shootingfee: 0, installationfee: 0, refundAmount: 0 },
      // dialogs
      timelineDialog: false,
      forwardDialog: false,
      rangeDialog: false,
      rejectDialog: false,
      proceedDialog: false,
      clerkDialog: false,
      rangeDoneDialog: false,
      refundDialog: false,
      uploadDialog: false,
      // dialog state
      // [{ label, verb, userId, person }] — one button each
      targets: [],
      targetsLoading: false,
      forwardTarget: null,
      reason: "",
      ranges: [],
      selectedRanges: [],
      refundtype: null,
      refundtypenumber: "",
      uploadSlot: null,
      uploadFile: null,
      // cancellation decision
      markPaidDialog: false,
      decisionDialog: false,
      decision: null,
      remarks: "",
    };
  },
  computed: {
    // A DFO opening the admin view of their own application gets the DFO desk.
    effectiveMode() {
      if (this.mode === "admin" && getUserType() === "division") return "division";
      return this.mode;
    },
    isDeskMode() {
      return this.effectiveMode !== "admin";
    },
    status() {
      return statusOf(this.app);
    },
    whereLabel() {
      if (!this.app) return "";
      if (this.app.status === "Cancelrequested") return "Awaiting DFO decision";
      if (this.app.status !== "Active") return this.app.status;
      // The desk it waits at (any officer in that role can act), not a name.
      return TASK_LABELS[this.app.task] || this.app.task;
    },
    // A cancelled application that was paid is refunded by the DFO by DD or cheque.
    canRecordRefund() {
      return this.refundTrackable && this.effectiveMode === "division" && this.app.refundStatus !== "Completed";
    },
    refundStep() {
      return this.app && this.app.refundStatus === "Completed" ? 3 : 1;
    },
    refundSteps() {
      const a = this.app || {};
      return [
        { label: "Cancellation accepted", sub: "Application closed" },
        { label: "Pay the refund", sub: "₹" + this.money(a.refundAmount || this.totalPaid) + " by DD or cheque" },
        { label: "Refund closed", sub: a.refundtypenumber ? a.refundtype + " no. " + a.refundtypenumber : "DD / cheque number recorded" },
      ];
    },

    refundTrackable() {
      const a = this.app;
      return !!a && a.status === "Cancelled" && a.paymentStatus === "Success";
    },
    // Super admin only; the server checks this again.
    // The security-deposit refund has been paid; no refund or send-back actions remain.
    refundDone() {
      return !!this.app && this.app.refundStatus === "Completed";
    },
    canMarkPaid() {
      return isSuperAdmin() && !!this.app && this.app.status === "Active" && this.app.paymentStatus !== "Success";
    },
    // The DFO decides an applicant's cancellation request on this same page.
    cancelPending() {
      return this.effectiveMode === "division" && !!this.app && this.app.status === "Cancelrequested";
    },
    hasCancelReason() {
      const r = this.app && this.app.reason;
      return !!r && r !== "Nill";
    },
    whereNow() {
      const label = this.app && TASK_LABELS[this.app.task];
      return label ? label.replace(/^With /, "the ") : "where it was";
    },
    totalPaid() {
      const a = this.app || {};
      return Number(a.shootingfee || 0) + Number(a.installationfee || 0) + Number(a.securitydeposit || 0);
    },
    locations() {
      return (this.app && this.app.filmShootingDetails) || [];
    },
    // Each animal in the shoot; older applications only have one species.
    animalList() {
      const a = this.app || {};
      if (a.animals && a.animals.length) return a.animals;
      return a.species ? [{ species: a.species, count: a.noOfAnimal }] : [];
    },
    payments() {
      return (this.app && this.app.paymentTransactions) || [];
    },
    showPayments() {
      return this.effectiveMode === "division" || this.effectiveMode === "admin";
    },
    hasRangeDocs() {
      const a = this.app;
      return !!(a && a.rangeMahassars && a.rangeMahassars.length && a.rangeReports && a.rangeReports.length);
    },
    waitingForPayment() {
      return !!(this.app && this.app.status === "Active" && this.app.paymentInitiation === "YES" && this.app.paymentStatus === "Pending");
    },
    applicantFields() {
      const a = this.app || {};
      return [
        { label: "Name", value: a.applicantName },
        { label: "Production house", value: a.nameofProductionhouse },
        { label: "Mobile", value: a.applicantMobileNo },
        { label: "Email", value: a.applicantEmail },
        { label: "District", value: a.applicantDistrict },
        { label: "Address", value: a.applicantAddress },
        { label: "Pincode", value: a.applicantPinCode },
        { label: "ID type", value: a.idType },
        { label: "ID number", value: a.idcardNo },
        { label: "Category", value: a.natureOfApplication },
        { label: "Submitted on", value: this.formatDate(a.created_at) },
      ].filter((f) => f.label !== "Production house" || f.value);
    },
    documents() {
      const a = this.app || {};
      const docs = [];
      const add = (label, key) => key && docs.push({ label, key });
      add("ID proof", a.idProof);
      if (a.animalinvolved === "YES") add("Pre-shoot order", a.Preshootfile);
      add("Additional document", a.additionalProof);
      add("Signed agreement", a.userAgreement);
      add("Proceedings", a.proceedings);
      add("Licence", a.license);
      add("Letter to range", a.letterToRange);
      (a.rangeMahassars || []).forEach((key, i) => add("Range mahazar " + (i + 1), key));
      (a.rangeReports || []).forEach((key, i) => add("Range report " + (i + 1), key));
      return docs;
    },
    canEditFees() {
      if (!this.canAct || !this.isDeskMode || this.effectiveMode === "range" || !this.app || this.app.status !== "Active") return false;
      if (this.app.refundStatus === "Completed") return false;
      return this.app.paymentStatus !== "Success" || (this.app.rangeMahassars && this.app.rangeMahassars.length > 0);
    },
    canGenerateOrders() {
      return this.app && this.app.task === "T6" && (this.effectiveMode === "division" || this.effectiveMode === "admin");
    },
    // Upload buttons under Documents. At the viewer's desk the task card
    // offers these instead, so this is mainly for admins.
    uploadSlots() {
      const a = this.app;
      if (!a || a.status !== "Active") return [];
      // Clerk uploads the executed agreement once payment is initiated.
      if (this.effectiveMode === "clerk" && this.canAct && a.paymentInitiation === "YES" && (!a.userAgreement || a.task === "T4")) {
        return [UPLOADS.userAgreement];
      }
      // DFO (and admin) upload the signed T6 orders.
      if (this.canGenerateOrders && (this.canAct || this.effectiveMode === "admin")) {
        return [UPLOADS.proceedings, UPLOADS.license, UPLOADS.letterToRange];
      }
      return [];
    },
    // Range offices that have uploaded both files.
    rangesDone() {
      const r = this.rangeState;
      return r ? Math.min(r.mahazars, r.reports) : 0;
    },
    // Shown in the action bar while a button there is locked.
    lockedNote() {
      return this.actions.some((a) => a.disabled) && this.taskGuide ? this.taskGuide.note : "";
    },
    forwardTitle() {
      const t = this.forwardTarget;
      return t ? t.verb + " " + t.label : "Forward application";
    },
    // One action button per destination, e.g. "Forward to DFO".
    targetActions() {
      return this.targets.map((t) => ({
        key: "to-" + t.label,
        label: t.verb + " " + t.label,
        icon: t.person ? "mdi-account-arrow-left-outline" : t.verb === "Forward to" ? "mdi-send-outline" : "mdi-undo-variant",
        tone: TARGET_TONES[t.label] || "primary",
        run: () => this.openForward(t),
      }));
    },
    // Clerk: fees entered (stage 1), and after payment the executed agreement
    // uploaded (the server refuses the forward without it).
    clerkCanForward() {
      const a = this.app || {};
      const feeEntered = Number(a.shootingfee) !== 0 || (a.rangeMahassars && a.rangeMahassars.length > 0);
      return feeEntered && (a.paymentInitiation !== "YES" || !!a.userAgreement);
    },
    // DFO at T6: the three signed orders are uploaded.
    ordersUploaded() {
      const a = this.app || {};
      return !!(a.proceedings && a.license && a.letterToRange);
    },
    // "Your task" card: what the viewer has to do at their desk now, with a
    // button to do each step, and the action buttons that unlocks.
    // steps: [{ label, sub, done, buttons }] where done null is optional;
    // next: [{ label, ready }]; note says when locked buttons open up.
    taskGuide() {
      const a = this.app;
      if (!a || !this.canAct || a.status !== "Active") return null;
      const mode = this.effectiveMode;
      const stage = stagesFor(mode).find((s) => s.task === a.task);
      const g = {
        tag: stage ? stage.tag : a.task,
        title: stage ? stage.name : TASK_LABELS[a.task] || "At your desk",
        hint: desks[mode] && desks[mode].tasks[a.task],
        steps: [],
        next: [],
        note: "",
      };
      const READY = "Use the buttons in the action bar at the bottom.";
      const editFees = { label: "Edit fees", icon: "mdi-pencil-outline", run: this.scrollToFees };
      const upload = (slot, done) => ({ label: done ? "Replace" : "Upload", icon: "mdi-upload", run: () => this.openUpload(slot) });

      if (mode === "clerk") {
        const paid = a.paymentInitiation === "YES";
        const fees = Number(a.shootingfee) !== 0;
        if (!paid) g.steps.push({ label: "Enter the fees", sub: "Shooting and installation fee, then Save fees", done: fees, buttons: [editFees] });
        if (paid && (a.task === "T4" || !a.userAgreement)) {
          g.steps.push({ label: "Upload the executed agreement", sub: "The agreement signed by the applicant", done: !!a.userAgreement, buttons: [upload(UPLOADS.userAgreement, !!a.userAgreement)] });
        }
        g.next.push({ label: "Forward to Superintendent", ready: this.clerkCanForward });
        g.note = this.clerkCanForward ? READY : paid ? "Available once the agreement is uploaded." : "Available once the fees are saved.";
      } else if (mode === "superintendent") {
        if (a.task === "T2") g.steps.push({ label: "Check the fees", sub: "Change them if needed", done: null, buttons: [editFees] });
        if (a.task === "T9") g.steps.push({ label: "Check the refund amount", sub: "Change it if needed", done: null, buttons: [{ ...editFees, label: "Edit refund" }] });
        g.next.push({ label: "Forward to DFO", ready: true }, { label: "Send back to Section Clerk", ready: true });
        g.note = READY;
      } else if (mode === "division") {
        if (a.task === "T3" && a.paymentStatus === "Success") {
          g.hint = "The fees are paid. Send it to the Section Clerk, who uploads the executed agreement.";
          g.next.push({ label: "Send to Section Clerk", ready: true });
          g.note = READY;
        } else if (a.task === "T3" && this.waitingForPayment) {
          g.hint = "Payment has been started. Once the applicant pays, the application moves to the Section Clerk on its own.";
          g.steps.push({ label: "Applicant pays ₹" + this.money(Number(a.shootingfee || 0) + Number(a.installationfee || 0)), sub: "Nothing to do until then", done: false });
          g.next.push({ label: "Reject", ready: true });
          g.note = "You can still reject it from the action bar.";
        } else if (a.task === "T3") {
          g.steps.push({ label: "Check the fees", sub: "Change them if needed", done: null, buttons: [editFees] });
          g.next.push({ label: "Proceed to payment", ready: true }, { label: "Return to Superintendent / Applicant", ready: true }, { label: "Reject", ready: true });
          g.note = READY;
        } else if (a.task === "T6") {
          // Generate each order, sign it, upload the signed copy.
          const order = (slot, label) => ({
            label,
            sub: a[slot.field] ? "Signed copy uploaded" : "Generate, sign, then upload the signed copy",
            done: !!a[slot.field],
            buttons: [
              { label: "Generate", icon: "mdi-file-pdf-box", tone: "doc", run: () => this.openPdf(slot.pdf) },
              upload(slot, !!a[slot.field]),
            ],
          });
          g.steps.push(
            order(UPLOADS.proceedings, "Signed proceedings"),
            order(UPLOADS.license, "Signed licence"),
            order(UPLOADS.letterToRange, "Signed letter to range")
          );
          g.next.push({ label: "Forward to range offices", ready: this.ordersUploaded });
          g.note = this.ordersUploaded ? READY : "Available once all three signed documents are uploaded.";
        } else if (a.task === "T10") {
          g.steps.push({ label: "Check the refund amount", sub: "Change it if needed", done: null, buttons: [{ ...editFees, label: "Edit refund" }] });
          g.next.push({ label: "Approve refund", ready: true }, { label: "Send back to ranges", ready: true });
          g.note = READY;
        }
      } else if (mode === "range" && this.rangeState) {
        const r = this.rangeState;
        const mineDone = r.mahazarDone && r.reportDone;
        const ready = mineDone && r.allDone;
        // Range offices still to upload, by name ("you" for the viewer's own).
        const waitingFor = joinNames(this.rangeProgress.filter((p) => !(p.mahazar && p.report)).map((p) => (p.you ? "you" : p.name)));
        const others = joinNames(this.rangeProgress.filter((p) => !p.you && !(p.mahazar && p.report)).map((p) => p.name));
        if (ready) {
          g.hint = "Every range office has uploaded its mahazar and report. Any of the range offices can now forward it to the Section Clerk.";
        } else if (mineDone) {
          g.hint = "Your mahazar and report are uploaded. Waiting for " + (others || "the other range offices") + ". Once they upload, any of the range offices can forward it to the Section Clerk.";
        }
        // A range office uploads each file once.
        g.steps.push(
          { label: "Upload your mahazar", sub: r.mahazarDone ? "Uploaded" : "The mahazar of your site inspection", done: r.mahazarDone, buttons: r.mahazarDone ? [] : [upload(UPLOADS.mahazar, false)] },
          { label: "Upload your report", sub: r.reportDone ? "Uploaded" : "Your inspection report", done: r.reportDone, buttons: r.reportDone ? [] : [upload(UPLOADS.report, false)] },
          {
            label: "Every range office uploads",
            sub: r.allDone ? "All " + r.total + " range offices have uploaded" : this.rangesDone + " of " + r.total + " done · waiting for " + (waitingFor || "the range offices"),
            done: r.allDone,
            ranges: this.rangeProgress,
          }
        );
        g.next.push({ label: "Forward to Section Clerk", ready });
        g.note = ready
          ? "Any of the range offices can forward it. " + READY
          : "Available once the mahazar and report are in from " + (waitingFor || "every range office") + ". Then any of the range offices can forward it.";
      }
      return g;
    },
    // Buttons in the sticky action bar. Those this stage leads to are always
    // shown; `disabled` ones open up once the task card's steps are done.
    actions() {
      const a = this.app;
      if (!a || !this.canAct || a.status !== "Active") return [];
      const list = [];
      const mode = this.effectiveMode;
      if (mode === "clerk") {
        list.push(...this.targetActions.map((t) => ({ ...t, disabled: !this.clerkCanForward })));
      } else if (mode === "superintendent") {
        list.push(...this.targetActions);
      } else if (mode === "division") {
        const beforePayment = a.paymentInitiation !== "YES" && a.paymentStatus === "Pending";
        if (beforePayment) {
          list.push(...this.targetActions);
          list.push({ key: "proceed", label: "Proceed to payment", icon: "mdi-cash-check", tone: "success", run: () => (this.proceedDialog = true) });
        }
        // Paid but never moved on from stage 1 (e.g. marked as paid): stage 2
        // starts with the Section Clerk uploading the executed agreement.
        if (a.task === "T3" && a.paymentStatus === "Success") {
          list.push({ key: "toClerk", label: "Send to Section Clerk", icon: "mdi-send-outline", tone: "primary", run: () => (this.clerkDialog = true) });
        }
        if (a.task === "T6" && a.paymentStatus === "Success" && !(a.rangeMahassars && a.rangeMahassars.length) && !(a.rangeReports && a.rangeReports.length)) {
          list.push({ key: "range", label: "Forward to range offices", icon: "mdi-map-marker-radius-outline", tone: "primary", run: this.openRange, disabled: !this.ordersUploaded });
        }
        if (this.hasRangeDocs && !this.refundDone) {
          list.push({ key: "rangeBack", label: "Send back to ranges", icon: "mdi-undo-variant", tone: "warning", run: this.openRange });
          list.push({ key: "refund", label: "Approve refund", icon: "mdi-cash-refund", tone: "success", run: () => (this.refundDialog = true) });
        }
        if (a.paymentStatus !== "Success") {
          list.push({ key: "reject", label: "Reject", icon: "mdi-close-octagon-outline", tone: "danger", run: () => { this.reason = ""; this.rejectDialog = true; } });
        }
      } else if (mode === "range" && this.rangeState) {
        // Moves on once every range office has uploaded both files.
        const r = this.rangeState;
        const ready = r.mahazarDone && r.reportDone && r.allDone;
        list.push({ key: "rangeDone", label: "Forward to Section Clerk", icon: "mdi-send-outline", tone: "primary", run: () => (this.rangeDoneDialog = true), disabled: !ready });
      }
      return list;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    headers() {
      return { token: localStorage.getItem("token") };
    },
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    load() {
      if (!this.id) {
        this.loading = false;
        return;
      }
      axios({ method: "POST", url: "/single/application", headers: this.headers(), data: { appid: this.id } })
        .then((response) => {
          this.loading = false;
          if (!response.data.status) {
            this.toast(response.data.msg);
            return;
          }
          this.app = response.data.data;
          this.canAct = !!response.data.canAct && this.isDeskMode;
          this.rangeState = this.effectiveMode === "range" ? response.data.range || null : null;
          this.rangeProgress = response.data.rangeProgress || [];
          if (this.canAct) this.loadTargets();
          this.fees = {
            shootingfee: this.app.shootingfee || 0,
            installationfee: this.app.installationfee || 0,
            refundAmount: this.app.refundAmount || 0,
          };
        })
        .catch((err) => {
          this.loading = false;
          console.log(err);
        });
    },
    // After an action that moves the application on, go back to the desk.
    done(message) {
      this.busy = false;
      this.$appAlert({ type: "success", title: "Done", message, highlight: "#" + this.app.applicationNo }).then(() => {
        const desk = desks[this.effectiveMode];
        if (desk) this.$router.push({ path: desk.listRoute });
        else this.load();
      });
    },
    failed(err) {
      this.busy = false;
      if (err && err.response && err.response.data && err.response.data.msg) this.toast(err.response.data.msg);
      else this.toast("Something went wrong. Please try again.");
      console.log(err);
    },
    post(url, data) {
      this.busy = true;
      return axios({ method: "POST", url, headers: this.headers(), data });
    },

    // ── Refund of a cancelled application ──
    openRefundDialog() {
      this.refundtype = null;
      this.refundtypenumber = "";
      this.refundDialog = true;
    },

    // ── Super admin: mark as paid ──
    openMarkPaid() {
      this.reason = "";
      this.markPaidDialog = true;
    },
    submitMarkPaid() {
      if (!this.reason || !this.reason.trim()) {
        this.toast("Please enter a reason.");
        return;
      }
      this.post("/superadmin/mark-paid", { appid: this.id, reason: this.reason.trim() })
        .then((response) => {
          this.busy = false;
          this.toast(response.data.msg);
          if (response.data.status) {
            this.markPaidDialog = false;
            this.load();
          }
        })
        .catch(this.failed);
    },

    // ── Cancellation request ──
    openDecision(kind) {
      this.decision = kind;
      this.remarks = "";
      this.decisionDialog = true;
    },
    submitDecision() {
      const accept = this.decision === "accept";
      this.post(accept ? "/division/approve/cancel" : "/division/reject/cancel", { appid: this.id, remarks: this.remarks })
        .then((response) => {
          this.busy = false;
          this.decisionDialog = false;
          if (!response.data.status) {
            this.$appAlert({ type: "error", title: "Couldn't save", message: response.data.msg });
            this.load();
            return;
          }
          this.$appAlert({
            type: "success",
            title: accept ? "Application cancelled" : "Request denied",
            message: accept ? "The application has been cancelled." : "The application stays active.",
            highlight: "#" + this.app.applicationNo,
          }).then(() => this.$router.push("/cancelrequests").catch(() => {}));
        })
        .catch(this.failed);
    },

    // ── Fees ──
    submitFees() {
      const f = this.fees;
      if ([f.shootingfee, f.installationfee, f.refundAmount].some((v) => v === "" || v == null || Number(v) < 0)) {
        this.toast("Please enter valid amounts.");
        return;
      }
      this.post("/addSecurityDepositAndShootingFee", {
        appid: this.id,
        securitydeposit: this.app.securitydeposit || 0,
        shootingfee: f.shootingfee,
        installationfee: f.installationfee,
        refundAmount: f.refundAmount,
      })
        .then((response) => {
          this.busy = false;
          this.toast(response.data.msg);
          if (response.data.status) this.load();
        })
        .catch(this.failed);
    },

    // ── Forward / return ──
    // Destinations for the action buttons: one per desk (role).
    loadTargets() {
      const source = TARGET_SOURCES[this.effectiveMode];
      if (!source) {
        this.targets = [];
        return;
      }
      this.targetsLoading = true;
      axios({ method: "POST", url: source.url, headers: this.headers(), data: { appid: this.id } })
        .then((response) => {
          this.targetsLoading = false;
          const d = response.data || {};
          const targets = [];
          source.groups.forEach(([many, one, label, verb]) => {
            const person = d[many] && d[many].length ? d[many][0] : d[one];
            if (person) targets.push({ label, verb, userId: person._id, person: false });
          });
          if (source.applicant && d.user) {
            targets.push({ label: "Applicant", verb: "Return to", userId: d.user._id, person: true, name: d.user.username });
          }
          this.targets = targets;
        })
        .catch((err) => {
          this.targetsLoading = false;
          this.targets = [];
          console.log(err);
        });
    },
    openForward(target) {
      this.forwardTarget = target;
      this.reason = "";
      this.forwardDialog = true;
    },
    submitForward() {
      if (!this.forwardTarget) {
        this.toast("Please choose who to send the application to.");
        return;
      }
      if (!this.reason || !this.reason.trim()) {
        this.toast("Please enter remarks.");
        return;
      }
      let url = "/forward/";
      if (this.effectiveMode === "division") url = "/division/firstforward/";
      else if (this.effectiveMode === "clerk" && this.app.paymentInitiation === "YES") url = "/clerk/forward";
      this.post(url, { appid: this.id, forwardto: this.forwardTarget.userId, reason: this.reason.trim() })
        .then((response) => {
          if (!response.data.status) {
            this.busy = false;
            this.toast(response.data.msg);
            return;
          }
          this.forwardDialog = false;
          this.done(response.data.msg);
        })
        .catch(this.failed);
    },

    // ── DFO ──
    submitProceed() {
      this.post("/division/payment/initiation/", { appid: this.id })
        .then((response) => {
          if (!response.data.status) {
            this.busy = false;
            this.toast(response.data.msg);
            return;
          }
          this.proceedDialog = false;
          this.busy = false;
          this.toast(response.data.msg);
          this.load();
        })
        .catch(this.failed);
    },
    submitReject() {
      if (!this.reason || !this.reason.trim()) {
        this.toast("Please enter the reason for rejection.");
        return;
      }
      this.post("/division/approval", { status: "no", reason: this.reason.trim(), appid: this.id })
        .then((response) => {
          if (!response.data.status) {
            this.busy = false;
            this.toast(response.data.msg);
            return;
          }
          this.rejectDialog = false;
          this.done(response.data.msg || "Application rejected.");
        })
        .catch(this.failed);
    },
    submitToClerk() {
      this.post("/check/forward", { appid: this.id })
        .then((response) => {
          if (!response.data.status) {
            this.busy = false;
            this.toast(response.data.msg);
            return;
          }
          this.clerkDialog = false;
          this.done("Sent to the Section Clerk to upload the executed agreement.");
        })
        .catch(this.failed);
    },
    openRange() {
      this.selectedRanges = [];
      this.rangeDialog = true;
      if (this.ranges.length) return;
      // Range offices under the DFO's division.
      axios({ method: "GET", url: "/profile", headers: this.headers() })
        .then((response) => {
          if (!response.data.status) return null;
          return axios({ method: "POST", url: "/get/range/v2", headers: this.headers(), data: { id: response.data.data._id } });
        })
        .then((response) => {
          if (response && response.data.status) this.ranges = response.data.data || [];
        })
        .catch((err) => console.log(err));
    },
    submitRangeForward() {
      if (!this.selectedRanges.length) {
        this.toast("Please select at least one range office.");
        return;
      }
      const missing = [["proceedings", "proceedings"], ["license", "licence"], ["letterToRange", "letter to range"]].find(([f]) => !this.app[f]);
      if (missing) {
        this.toast("Please upload the signed " + missing[1] + " first.");
        return;
      }
      this.post("/range/forward/", { appid: this.id, rangeid: this.selectedRanges })
        .then((response) => {
          if (!response.data.status) {
            this.busy = false;
            this.toast(response.data.msg);
            return;
          }
          this.rangeDialog = false;
          this.done(response.data.msg);
        })
        .catch(this.failed);
    },
    submitRefund() {
      if (!this.refundtype) {
        this.toast("Please choose how the refund was paid.");
        return;
      }
      if (!/^\d{6}$/.test(this.refundtypenumber || "")) {
        this.toast("Please enter the 6-digit DD / cheque number.");
        return;
      }
      this.post("/challan/refund-details", {
        appid: this.id,
        refundStatus: "Completed",
        refundtype: this.refundtype,
        refundtypenumber: this.refundtypenumber,
      })
        .then((response) => {
          if (!response.data.status) {
            this.busy = false;
            this.toast(response.data.msg);
            return;
          }
          this.refundDialog = false;
          this.done(response.data.msg || "Refund approved.");
        })
        .catch(this.failed);
    },

    // ── Range officer ──
    submitRangeDone() {
      this.post("/range/forward/clerk", { appid: this.id })
        .then((response) => {
          if (!response.data.status) {
            this.busy = false;
            this.toast(response.data.msg);
            return;
          }
          this.rangeDoneDialog = false;
          this.done(response.data.msg);
        })
        .catch(this.failed);
    },

    // What one range office has uploaded so far.
    rangeUploadLabel(r) {
      if (r.mahazar && r.report) return "Uploaded";
      if (r.mahazar) return "Report pending";
      if (r.report) return "Mahazar pending";
      return "Waiting";
    },
    // "Edit fees" in the task card: bring the fee editor into view.
    scrollToFees() {
      const panel = this.$refs.feesPanel;
      if (panel) panel.scrollIntoView({ behavior: "smooth", block: "center" });
    },

    // ── Documents ──
    fileUrl(key) {
      return this.baseURL + "file/get/" + key;
    },
    openUpload(slot) {
      this.uploadSlot = slot;
      this.uploadFile = null;
      this.uploadDialog = true;
    },
    submitUpload() {
      const slot = this.uploadSlot;
      const form = new FormData();
      form.append(slot.field, this.uploadFile);
      form.append("appid", this.id);
      this.busy = true;
      axios
        .post(slot.url, form, { headers: this.headers() })
        .then((response) => {
          // Two-step uploads: confirm the saved file by its key.
          if (!slot.confirmUrl || !response.data.status) return response;
          const confirm = new FormData();
          confirm.append("appid", this.id);
          confirm.append("key", response.data.key);
          return axios.post(slot.confirmUrl, confirm, { headers: this.headers() });
        })
        .then((response) => {
          this.busy = false;
          this.toast(response.data.msg);
          if (response.data.status !== false) {
            this.uploadDialog = false;
            this.load();
          }
        })
        .catch(this.failed);
    },
    // Generated PDFs: open a tab first so pop-up blockers allow it.
    openPdf(url) {
      const tab = window.open("", "_blank");
      axios({
        method: "GET",
        url,
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        params: { appid: this.id },
        responseType: "blob",
      })
        .then((response) => {
          const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
          if (tab) tab.location.href = blobUrl;
          else window.open(blobUrl, "_blank");
        })
        .catch((err) => {
          if (tab) tab.close();
          this.toast("Could not open the PDF.");
          console.log(err);
        });
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    formatDate(value) {
      return value ? moment(value).format("D MMM YYYY") : "—";
    },
  },
};
</script>

<style scoped>
.where-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: -6px 0 18px;
}

.where-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 12.5px;
  font-weight: 700;
}

.where-chip .v-icon {
  color: inherit !important;
}

.where-chip--green {
  background: #dcfce7;
  color: #15803d;
}

.where-chip--amber {
  background: #fef3c7;
  color: #92400e;
}

/* Your task */
.tg-card {
  padding: 18px 20px;
  border-left: 4px solid #15803d;
}

.tg-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.tg-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #15803d, #166534);
}

.tg-head-text {
  min-width: 0;
}

.tg-kicker {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #15803d;
}

.tg-title {
  font-size: 17px;
  font-weight: 800;
  color: var(--ink);
}

.tg-hint {
  margin-top: 2px;
  font-size: 13.5px;
  color: var(--ink-muted);
}

/* Steps: side by side on wide screens, stacked on phones */
.tg-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}

.tg-step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #ffffff;
}

.tg-step-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
  font-weight: 800;
}

.tg-step--done {
  background: #f3faf5;
  border-color: #cfe8d6;
}

.tg-step--done .tg-step-dot {
  background: #15803d;
}

.tg-step--optional .tg-step-dot {
  background: #f1f5f9;
  color: #475569;
}

.tg-step-text {
  min-width: 0;
}

.tg-step-label {
  font-size: 13.5px;
  font-weight: 800;
  color: var(--ink);
}

.tg-step-sub {
  font-size: 12.5px;
  color: var(--ink-muted);
}

.tg-step-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.tg-ranges {
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}

.tg-ranges li {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  font-size: 12.5px;
  color: #92400e;
}

.tg-ranges li .v-icon {
  color: inherit !important;
}

.tg-ranges li.tg-range--done {
  color: #15803d;
}

.tg-range-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
  color: var(--ink);
}

.tg-range-state {
  margin-left: auto;
  font-weight: 700;
  white-space: nowrap;
}

.fee-row b.rng-ok {
  color: #15803d;
}

.fee-row b.rng-wait {
  color: #b45309;
}

.tg-optional {
  margin-left: 4px;
  padding: 0 6px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
}

.tg-next {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #e3eee6;
}

.tg-next-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.tg-action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
}

.tg-action .v-icon {
  color: inherit !important;
}

.tg-action--ready {
  background: #dcfce7;
  color: #166534;
}

.tg-action--locked {
  background: #f1f5f9;
  color: #64748b;
}

.tg-note {
  font-size: 12.5px;
  color: var(--ink-muted);
}

.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 20px;
  align-items: start;
}

.fee-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed #e3eee6;
  font-size: 14px;
  color: var(--ink-muted);
}

.fee-row b {
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.fee-editor {
  margin-top: 16px;
  padding: 14px;
  border-radius: 14px;
  background: #f3faf5;
  border: 1px solid #d7ebdd;
}

.fee-editor-title,
.doc-title {
  margin: 14px 0 10px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.fee-editor-title {
  margin-top: 0;
}

/* As many tiles per row as fit the main column */
.doc-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}

/* Generated PDFs and uploads side by side when there is room */
.doc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0 40px;
  margin-top: 6px;
}

.doc-link {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.doc-link:hover {
  background: #f3faf5;
  border-color: rgba(21, 128, 61, 0.35);
}

.doc-link-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pdf-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sticky-actions.surface {
  border-radius: 16px;
  margin-top: 4px;
}

.dialog-card {
  border-radius: 18px !important;
  overflow: hidden;
}

.fwd-to {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: #f7fbf8;
}

.fwd-to-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 12px;
}

.fwd-to-icon .v-icon {
  color: inherit !important;
}

.fwd-to-icon--desk {
  background: #dcfce7;
  color: #15803d;
}

.fwd-to-icon--person {
  background: #ede9fe;
  color: #6d28d9;
}

.fwd-to-name {
  font-size: 15px;
  font-weight: 800;
  color: var(--ink);
}

.fwd-to-sub {
  font-size: 12.5px;
  color: var(--ink-muted);
}

.note-warn {
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fef3c7;
  color: #92400e;
  font-size: 13px;
  font-weight: 600;
}

@media (max-width: 1100px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  /* Stacked below the main column: side panels next to each other */
  .detail-side {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    column-gap: 20px;
    align-items: start;
  }
}

/* Refund */
.rf-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.rf-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}

.rf-steps li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  opacity: 0.55;
}

.rf-steps li.rf-done,
.rf-steps li.rf-now {
  opacity: 1;
}

.rf-dot {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  background: #e2e8f0;
  color: #475569;
}

.rf-done .rf-dot {
  background: #15803d;
}

.rf-now .rf-dot {
  background: #ea580c;
  color: #ffffff;
}

.rf-label {
  font-size: 13.5px;
  font-weight: 800;
  color: #0f172a;
}

.rf-sub {
  font-size: 12px;
  color: #64748b;
}

@media (max-width: 800px) {
  .rf-steps {
    grid-template-columns: minmax(0, 1fr);
  }
}

.rf-paid {
  margin: 14px 0 0;
  font-size: 13.5px;
  font-weight: 700;
  color: #15803d;
}

/* Cancellation request */
.cr-banner {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  padding: 18px 20px;
  border-radius: 18px;
  background: linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%);
  border: 1px solid #fed7aa;
}

.cr-banner-icon {
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ea580c 0%, #b91c1c 100%);
  box-shadow: 0 8px 18px rgba(185, 28, 28, 0.22);
}

.cr-banner-icon .v-icon {
  color: #ffffff !important;
}

.cr-banner--done {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.cr-banner--done .cr-banner-icon {
  background: #64748b;
  box-shadow: none;
}

.cr-banner-body {
  flex: 1;
  min-width: 0;
}

.cr-banner-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.cr-banner-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.cr-ref {
  padding: 2px 10px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 12px;
  font-weight: 800;
}

.cr-reason {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #fde4c8;
  border-left: 4px solid #ea580c;
}

.cr-reason-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #9a3412;
}

.cr-reason p {
  margin: 2px 0 0;
  font-size: 14.5px;
  font-weight: 600;
  color: #1f2937;
  white-space: pre-line;
  word-break: break-word;
}

.cr-outcomes {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  font-size: 13px;
  color: #475569;
}

.cr-outcomes li {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.cr-outcomes li + li {
  margin-top: 4px;
}

.cr-outcomes b {
  color: #0f172a;
}

.cr-paid {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  font-size: 12.5px;
  font-weight: 700;
}

.cr-paid .v-icon {
  color: inherit !important;
}

.cr-done-text {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 14px;
  color: #475569;
}

.cr-decide {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ink-muted);
}

.cr-dialog-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px;
  color: #ffffff;
}

.cr-dialog-head--accept {
  background: linear-gradient(120deg, #7f1d1d 0%, #b91c1c 100%);
}

.cr-dialog-head--deny {
  background: linear-gradient(120deg, #052e16 0%, #15803d 100%);
}

.cr-dialog-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.16);
}

.cr-dialog-title {
  font-size: 17px;
  font-weight: 800;
}

.cr-dialog-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

.cr-dialog-text {
  font-size: 14px;
  color: #334155;
}

@media (max-width: 600px) {
  .cr-banner {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 14px;
  }

  .cr-decide {
    width: 100%;
  }
}
</style>
